<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div class="asd">
      <div v-if="loading" class="d-flex justify-content-center"><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></div>
      <div v-if="fetchError" class="alert alert-danger d-flex align-items-center gap-2">{{ $t('materialstorage.fetch_error') }}<button class="btn btn-sm btn-outline-danger" @click.stop="fetchMaterialTypes">{{ $t('materialstorage.retry') }}</button></div>
      <table class="table table-sm" style="table-layout: fixed; overflow: visible;">
          <colgroup>
              <col style="width: 40%">
              <col style="width: 12%">
              <col style="width: 15%">
              <col style="width: 23%">
              <col style="width: 10%">
          </colgroup>
          <thead>
          <tr>
              <th v-for="field in fields" :key="field.key">{{ field.label }}</th>
              <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in tableItems" :key="index" style="height: 48px;">
              <td style="overflow: visible; position: relative; vertical-align: top; min-width: 160px;">
                  <div v-if="editable">
                      <!-- When materials are available: vue-multiselect single-select with filter -->
                      <multiselect
                          v-if="materials.length > 0 && (item.material_type_id || !item.name)"
                          :model-value="selectedMaterial(item)"
                          class="multiselect_routa multiselect-sm"
                          :placeholder="$t('materialstorage.record.select_material')"
                          :deselect-label="$t('common.remove_selection')"
                          :select-label="$t('common.select')"
                          :selected-label="$t('common.selected')"
                          label="name"
                          track-by="id"
                          :searchable="true"
                          :internal-search="true"
                          :allow-empty="true"
                          :options="materials"
                          @select="onMultiselectSelect(item, $event)"
                          @remove="clearMaterial(item)"
                      >
                          <template #noResult>
                              <span>{{ $t('common.not_found') }}</span>
                          </template>
                          <template #noOptions>
                              <span>{{ $t('common.no_results') }}</span>
                          </template>
                      </multiselect>
                      <!-- Legacy free-text record or no materials: plain text input -->
                      <input
                          v-else
                          v-model="item.name"
                          type="text"
                          class="form-control form-control-sm"
                          :aria-invalid="!item.name"
                      />
                      <div v-if="materials.length > 0 && !item.material_type_id && !item.name" class="text-danger small mt-1">
                          {{ $t('materialstorage.record.material_required') }}
                      </div>
                  </div>
                  <span v-else>{{ resolvedName(item) }}</span>
              </td>
              <td>
                  <div v-if="editable">
                      <input
                          :value="item.amount"
                          type="number"
                          step="any"
                          class="form-control form-control-sm"
                          @input="e => item.amount = Number(e.target.value)"
                      />
                  </div>
                  <span v-else>{{ item.amount }}</span>
              </td>
              <td>
                  <div v-if="editable">
                      <select
                          v-model="item.unit"
                          class="form-select form-select-sm"
                          :required="editable"
                          :disabled="item.material_type_id && !!selectedMaterial(item)?.unit"
                          :aria-invalid="validateUnit(item.unit) !== null"
                      >
                          <option v-for="unit in units" :key="unit.value" :value="unit.value">{{ unit.text }}</option>
                      </select>
                  </div>
                  <span v-else>{{ item.unit }}</span>
                  <div v-if="validateUnit(item.unit) !== null">
                      {{ $t('materialstorage.unit_required') }}
                  </div>
              </td>
              <td>
                  <div v-if="editable">
                      <select
                          v-model="item.status"
                          class="form-select form-select-sm"
                          @input="e => item.status = Number(e.target.value)"
                      >
                          <option v-for="(status, index) in statuses" :key="index" :value="index">{{ status }}</option>
                      </select>
                  </div>
                  <span v-else>{{ statuses[item.status] }}</span>
              </td>
              <td>
                  <button
                      v-if="editable"
                      class="btn btn-danger"
                      @click.stop="inactivateMaterial(item.id, item.name)"
                  >
                      <i class="fa fa-times" />
                  </button>
              </td>
          </tr>
          </tbody>
      </table>
      <div class="col-12 d-flex justify-content-end">
          <button
              v-if="editable"
              class="btn btn-sm btn-success"
              @click.stop="addNewMaterial()"
          >
              <span>{{ $t('materialstorage.record.add_new') }}</span>
          </button>
      </div>
      <div
          v-if="inactiveItems && inactiveItems.length > 0"
          class="col-12 nopads"
      >
          <span class="span-title">{{ $t('materialstorage.inactive_materials') }}</span>
          <table class="table table-sm table-responsive">
              <thead>
              <tr>
                  <th v-for="field in fields" :key="field.key">{{ field.label }}</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, index) in inactiveItems" :key="index">
                  <td v-for="field in fields" :key="field.key">
                      <span>{{ item[field.key] }}</span>
                  </td>
                  <td>
                      <div class="button-container">
                          <button
                              class="btn btn-primary btn-sm"
                              :disabled="!editable"
                              @click.stop="activateMaterial(item.id)"
                          >
                              {{ $t('materialstorage.restore') }}
                          </button>
                      </div>
                  </td>
              </tr>
              </tbody>
          </table>
      </div>
  </div>
</template>

<script>
import {newMaterialRecord, Status} from './MaterialStorageHelper'
import {restApi} from '@/components/mixins/RestApiMixin'
import Multiselect from 'vue-multiselect'

export default {
    name: 'MaterialRecordList',
    components: {Multiselect},
    mixins: [restApi],
    props: {
        modelValue: {
            type: Object,
            default: null
        },
        editable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editing: [],
            tableItems: [],
            inactiveItems: [],
            materials: [],
            loading: false,
            fetchError: false,
            units : [
                {value: 'kpl', text: this.$t('unit.pcs')},
                {value: 'kg', text: this.$t('unit.kg')},
                {value: 't', text: this.$t('unit.tons')},
                {value: 'm', text: this.$t('unit.meters')},
                {value: 'km', text: this.$t('unit.km')},
                {value: 'm2', text: this.$t('unit.square_m')},
                {value: 'm3', text: this.$t('unit.cubic_m')},
                {value: 'l', text: this.$t('unit.liter')},
            ]
        }
    },
    computed: {
        fields() {
            return [
                {
                    key: 'name',
                    label: this.$t('materialstorage.record.name'),
                },
                {
                    key: 'amount',
                    label: this.$t('materialstorage.record.amount'),
                },
                {
                    key: 'unit',
                    label: this.$t('materialstorage.record.unit'),
                },
                {
                    key: 'status',
                    label: this.$t('materialstorage.record.status'),
                }
            ]
        },
        statuses() {
            return {
                [Status.AVAILABLE]: this.$t('materialstorage.record.status_available'),
                [Status.ORDERED]: this.$t('materialstorage.record.status_ordered'),
                [Status.OUT_OF_STOCK]: this.$t('materialstorage.record.status_out_of_stock')
            }
        }
    },
    mounted() {
        this.initTableItems()
        this.fetchMaterialTypes()
    },
    methods: {
        validateUnit(unit) {
            if (this.editable && !unit) {
                return false;
            }
            return null;
        },
        initTableItems() {
            this.tableItems = this.modelValue.filter(item => item.active === true)
            this.inactiveItems = this.modelValue.filter(item => item.active === false)
        },
        activateMaterial(id) {
            let material = this.modelValue.find(item => item.id === id)
            if (material) {
                material.active = true
                this.initTableItems()
            }
        },
        inactivateMaterial(id, name) {
            if (id) {
                let material = this.modelValue.find(item => item.id === id)
                if (material) {
                    material.active = false
                    this.initTableItems()
                }
            } else {
                // Remove material from array - not added to backend yet
                const pos = this.modelValue.findIndex(item => item.name === name)
                if (pos >= 0) {
                    this.modelValue.splice(pos, 1)
                    this.initTableItems()
                }
            }
        },
        validateName(item) {
            if (this.editable && (!item.material_type_id || item.material_type_id === '')) {
                return false
            }
            return null
        },
        selectedMaterial(item) {
            if (!item.material_type_id) return null
            return this.materials.find(m => m.id == item.material_type_id) || null
        },
        onMultiselectSelect(item, entity) {
            item.material_type_id = entity.id
            item.name = entity.name
            if (entity.unit) item.unit = entity.unit
        },
        clearMaterial(item) {
            item.material_type_id = null
            item.name = ''
        },
        resolvedName(item) {
            if (this.materials.length > 0 && item.material_type_id) {
                const entity = this.materials.find(m => m.id === item.material_type_id)
                return entity ? entity.name : item.name
            }
            return item.name
        },
        addNewMaterial() {
            this.modelValue.push(this.newMaterialRecord(''))
            this.initTableItems()
        },
        newMaterialRecord,
        fetchMaterialTypes() {
            this.loading = true
            this.fetchError = false
            this.restFetch(this.materialListUrl, this.onFetchSuccess, this.onFetchFail)
        },
        onFetchSuccess(response) {
            this.materials = response.data
            this.loading = false
            this.fetchError = false
            // For existing records that have a name but no material_type_id,
            // try to match against the fetched list and restore the link
            this.modelValue.forEach(item => {
                if (!item.material_type_id && item.name) {
                    const match = this.materials.find(m => m.name === item.name)
                    if (match) {
                        item.material_type_id = match.id
                    }
                }
            })
        },
        onFetchFail() {
            this.loading = false
            this.fetchError = true
        },
    }
}
</script>

<style scoped>
.asd {
    display: block;
    height: auto;
}
.multiselect-sm {
    min-height: 31px;
    font-size: 0.875rem;
}
/* Prevent multiselect dropdown from reflowing the table */
:deep(.multiselect__content-wrapper) {
    position: absolute;
    z-index: 1050;
    width: 100%;
}
:deep(.multiselect) {
    min-height: 31px;
    box-sizing: border-box;
}
:deep(.multiselect__tags) {
    min-height: 31px;
    max-height: 31px;
    padding: 3px 40px 0 8px;
    font-size: 0.875rem;
    overflow: hidden;
}
:deep(.multiselect__single),
:deep(.multiselect__placeholder) {
    font-size: 0.875rem;
    line-height: 22px;
    margin-bottom: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
:deep(.multiselect__select) {
    height: 31px;
}
</style>

